import { useEffect, useState } from 'react';
import http from '../../../utils/SupabaseHttp';
import TranscriptionLayout from '../../../components/dashboard/TranscriptionLayout';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import CustomDrawer from '../../../components/drawer';
import Button from '../../../components/shared/Button';
import jsPDF from 'jspdf';
import { useQuery } from '@tanstack/react-query';
import { TranscriptionCostModal } from '../../../components/transcription/TranscriptionCostModal';
import { useTranscriptionSocket } from '../../../hooks/useTranscriptionSocket';

interface Transcription {
  id: string;
  job_name: string;
  media_format: string;
  status: string;
  created_at: string;
  transcript?: any;
  title: string
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TranscriptionResponse {
  success: boolean;
  data: Transcription[];
  pagination: PaginationData;
}

interface ClinicalDocumentation {
  SectionName: string;
  Summary?: Array<{
    SummarizedSegment?: string;
    EvidenceLinks?: Array<{
      SegmentId: string
    }>
  }>
}


const DocumentationDashboard = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // WebSocket hook for transcription completion
  const { transcriptionCompleted, clearTranscriptionCompleted } = useTranscriptionSocket();

  const fetchTranscriptions = async (page: number) => {
    const response = await http.get<TranscriptionResponse>(`/transcription/user?page=${page}&limit=10`);
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || null
    };
  };

  const {
    data: transcriptionsData,
    isLoading,
    error,
    isError,
    refetch
  } = useQuery({
    queryKey: ['transcriptions', currentPage],
    queryFn: () => fetchTranscriptions(currentPage),
    // keepPreviousData: true
  });

  const transcriptions = transcriptionsData?.data || [];
  const pagination = transcriptionsData?.pagination || null;


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewTranscription = async (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setDrawerOpen(true);

    // If transcription doesn't have transcript data, fetch it
    if (!transcription.transcript && transcription.status === 'COMPLETED') {
      setTranscriptLoading(true);
      try {
        const response = await http.get(`/transcription/${transcription.id}`);
        setSelectedTranscription({
          ...transcription,
          transcript: response.data.transcript
        });
      } catch (err) {
        console.error('Error fetching transcript:', err);
      } finally {
        setTranscriptLoading(false);
      }
    }
  };

  const handleDownloadPDF = () => {
    if (!selectedTranscription?.transcript?.ClinicalDocumentation) return;

    const content = document.getElementById("transcript-content");
    if (content) {
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = content.innerHTML;
      tempContainer.style.transform = 'scale(0.6)';
      tempContainer.style.transformOrigin = 'top left';
      tempContainer.style.width = '120%';
      tempContainer.style.fontSize = '11px';

      document.body.appendChild(tempContainer);

      const pdf = new jsPDF();
      pdf.html(tempContainer, {
        callback: function (doc) {
          document.body.removeChild(tempContainer);
          doc.save(`documentation-${selectedTranscription.title}.pdf`);
        },
        margin: [10, 10, 10, 10],
        x: 10,
        y: 5,
        autoPaging: "text",
        width: 200,
        html2canvas: {
          scale: 0.5,
          useCORS: true
        }
      });
    }
  };

  const renderTranscriptContent = () => {
    if (!selectedTranscription) return null;

    if (selectedTranscription.status === 'PROCESSING') {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Documentation is still being processed...</p>
        </div>
      );
    }

    if (selectedTranscription.status === 'FAILED') {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <p className="text-gray-600">Documentation processing failed. Please try again.</p>
        </div>
      );
    }

    const clinicalDoc = selectedTranscription.transcript?.ClinicalDocumentation;
    if (!clinicalDoc || !clinicalDoc.Sections) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-600">No documentation available.</p>
        </div>
      );
    }

    return (
      <div id="transcript-content" className="space-y-6">
        <div className="mb-6">
          <h3 className="text-[15px] font-semibold text-gray-800 mb-2">{selectedTranscription.title}</h3>
          <p className="text-xs text-gray-500">Created: {formatDate(selectedTranscription.created_at)}</p>
        </div>

        {clinicalDoc.Sections.map((section: ClinicalDocumentation, index: number) => (
          <div key={index} className="border-l-4 border-blue-500 pl-4">
            <h4 style={{ wordSpacing: '4px' }} className="font-semibold text-[14px] text-gray-800 mb-2">
              {section.SectionName.replace(/_/g, ' ')}
            </h4>
            {section.Summary && section.Summary.length > 0 ? (
              <ul className="space-y-2">
                {section.Summary.map((summary: any, summaryIndex: number) => (
                  <li key={summaryIndex} className="text-gray-700 text-[12px]">
                    • {summary.SummarizedSegment}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-[12px] italic">No information available for this section.</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Failed to fetch transcriptions. Please try again later.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white  pb-14 shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting caption
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Media Format
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transcriptions.length > 0 ? (
                  transcriptions.map((transcription) => (
                    <tr
                      key={transcription.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewTranscription(transcription)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transcription.title || transcription.job_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">.{transcription.media_format}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transcription.status)}`}>
                          {transcription.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transcription.created_at)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                      No transcriptions found. Upload a new file to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <CustomDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        drawerTitle={selectedTranscription?.title || selectedTranscription?.job_name || "Transcription Details"}
        width={800}
      >
        <div className="p-6">
          {/* Download Button */}
          {selectedTranscription?.status === 'COMPLETED' && selectedTranscription?.transcript?.ClinicalDocumentation && (
            <div className=" flex justify-end">
              <Button
                onClick={handleDownloadPDF}
                variant="primary"
                label="Download PDF"
              // size="small"
              />
            </div>
          )}

          {/* Transcription Content */}
          {renderTranscriptContent()}
        </div>
      </CustomDrawer>

      {/* Transcription Cost Modal */}
      <TranscriptionCostModal
        isVisible={!!transcriptionCompleted}
        onClose={clearTranscriptionCompleted}
        transcriptionData={transcriptionCompleted}
      />
    </DashboardLayout>
  );
};

export default DocumentationDashboard;